import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, Award, Users, Target, Heart, Zap, 
  ArrowRight, Quote, CheckCircle2, Phone
} from 'lucide-react';

export default function AboutPage() {
  const values = [
    {
      icon: Target,
      title: 'Proactive, Not Reactive',
      description: 'We prevent problems before they happen. Our monitoring catches issues while they\'re small, saving you time and money.',
    },
    {
      icon: Heart,
      title: 'Genuinely Care',
      description: 'Your success is our success. We treat your business like our own and go the extra mile to solve problems right.',
    },
    {
      icon: Shield,
      title: 'No BS Pricing',
      description: 'What you see is what you pay. No hidden fees, no surprise bills, no confusing invoices.',
    },
    {
      icon: Users,
      title: 'Real People, Real Support',
      description: 'When you call, you get a real technician who knows your business — not a overseas call center.',
    },
  ];

  const certifications = [
    'Microsoft Certified Partner',
    'Cisco Certified Network Associate',
    'CompTIA Security+',
    'AWS Certified Solutions Architect',
    'VMware Certified Professional',
    'HIPAA Compliance Certified',
  ];

  const testimonials = [
    {
      quote: 'MLC TECH transformed our IT from a constant headache to something we never have to think about. Their team responds fast and actually fixes problems the first time.',
      author: 'Sarah Mitchell',
      role: 'Operations Director',
      company: 'Phoenix Medical Group',
    },
    {
      quote: 'We switched from a big-name MSP and cut our IT costs by 40% while getting better service. The team at MLC TECH actually understands small business needs.',
      author: 'David Chen',
      role: 'CEO',
      company: 'Apex Manufacturing',
    },
    {
      quote: 'After a ransomware scare, MLC TECH helped us implement proper security and backup systems. I sleep better knowing our data is protected.',
      author: 'Jennifer Torres',
      role: 'Office Manager',
      company: 'Southwest Law Associates',
    },
  ];

  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '100+', label: 'Businesses Served' },
    { value: '99.9%', label: 'Client Retention' },
    { value: '15 min', label: 'Avg Response Time' },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-slate-950 py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-300">About MLC TECH</span>
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl font-black text-white mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                IT Support Built for{' '}
                <span className="text-gradient">Real Businesses</span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8">
                We started MLC TECH because we saw small businesses getting ripped off 
                by IT companies charging enterprise prices for mediocre service. 
                We knew there had to be a better way.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30"
                    data-testid="about-contact-btn"
                  >
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="MLC TECH Team"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-blue-600" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-3xl md:text-4xl font-black text-white mb-1"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  {stat.value}
                </div>
                <div className="text-sm text-blue-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-black text-slate-900 mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Our Mission
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              To provide small and medium businesses with enterprise-grade IT support 
              at prices they can actually afford — with the personal service and 
              responsiveness they deserve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <Card 
                key={value.title} 
                className="border border-slate-200 hover:border-blue-500/30 transition-colors"
                data-testid={`value-card-${value.title.toLowerCase().replace(/[\s,]/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 
                    className="text-lg font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-slate-50" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-3xl sm:text-4xl font-black text-slate-900 mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Why We Started MLC TECH
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Before starting MLC TECH, our founders spent years working for large 
                  IT companies. We saw firsthand how small businesses were treated as 
                  an afterthought — long wait times, confusing bills, and technicians 
                  who didn't care about solving problems.
                </p>
                <p>
                  We knew that small businesses deserved better. They needed the same 
                  quality of IT support that big companies get, but at prices that 
                  made sense for their budgets.
                </p>
                <p>
                  That's why we built MLC TECH from the ground up to serve SMBs. We 
                  keep our overhead low, our team skilled, and our focus laser-sharp 
                  on delivering real value — not just collecting monthly fees.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-blue-50 border border-blue-100 rounded-xl">
                <h4 className="font-bold text-slate-900 mb-2">Our Guarantee</h4>
                <p className="text-slate-600 text-sm">
                  If we can't solve your IT problem, you don't pay. If you're not 
                  satisfied with our service in the first 30 days, we'll refund your 
                  money. No questions asked.
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <h3 
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Certifications & Partnerships
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {certifications.map((cert) => (
                  <div key={cert} className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{cert}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <img 
                  src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Security Focus"
                  className="rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-900" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-400">
              Don't just take our word for it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="bg-slate-800 border-slate-700"
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-blue-500 mb-4" />
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-slate-700 pt-4">
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-slate-400">{testimonial.role}</p>
                    <p className="text-sm text-blue-400">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-mesh" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-black text-slate-900 mb-6"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Ready to Work with a Team That Cares?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Get a free IT assessment and see how MLC TECH can help your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/20 btn-hover-lift"
                data-testid="about-bottom-cta"
              >
                Schedule Free Assessment
              </Button>
            </Link>
            <a href="tel:6026491314">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-300"
              >
                <Phone className="w-4 h-4 mr-2" />
                (602) 649-1314
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
